<template>
  <v-form
    ref="form"
    v-model="valid"
    lazy-validation
    @submit.prevent="submit"
    @keypress.enter="submit"
  >
    <p class="text-justify mb-5">
      Qual método deseja utilizar para recuperar sua senha?
    </p>

    <v-list>
      <v-list-item
        v-for="(method, index) in methods"
        :key="method.name"
        class="mb-4 rounded"
        style="border: 1px solid #ccc"
        @click="selected = index"
      >
        <template #prepend>
          <v-checkbox
            :model-value="selected === index"
            color="primary"
            readonly
            hide-details
          />
        </template>

        <v-list-item-title> {{ method.value }} </v-list-item-title>
        <v-list-item-subtitle> {{ method.name }} </v-list-item-subtitle>
      </v-list-item>
    </v-list>

    <t-btn
      block
      color="success"
      type="submit"
      class="heading--text"
      large
      :loading="loading"
      :disabled="!valid"
    >
      Avançar
    </t-btn>
  </v-form>
</template>

<script setup lang="ts">
import { ref, computed, useTemplateRef, type Ref } from "vue";
import type User from "@/types/User";
import TBtn from "@/components/core/TBtn.vue";
import sendPasswordRecoveryCode from "@/api/requests/sendPasswordRecovery";
import axios, { type AxiosError } from "axios";
import type ErrorResponse from "@/types/ErrorResponse";
import type RecoveryMethod from "@/types/RecoveryMethod";
import { useSnackbar } from "@/stores/snackbar";

interface Props {
  modelValue?: User;
  methods: RecoveryMethod[];
}

const {
  modelValue = {} as User,
  methods,
} = defineProps<Props>();

const emit = defineEmits<{
  submit: [method: RecoveryMethod];
}>();

defineOptions({
  name: "FormGetRecoverCode",
});

const snackbar = useSnackbar();
const form = useTemplateRef<{ validate: () => boolean }>("form");

const valid: Ref<boolean> = ref(true);
const loading: Ref<boolean> = ref(false);
const selected: Ref<number> = ref(0);

const user = computed<User>(() => modelValue);

async function submit(): Promise<void> {
  try {
    if (form.value?.validate()) {
      loading.value = true;
      await sendPasswordRecoveryCode(user.value.document, methods[selected.value]);
      emit("submit", methods[selected.value]);
    }
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const error = e as AxiosError<ErrorResponse>;

      if (error.response?.data.message) {
        snackbar.show(error.response?.data.message, "error");
      } else {
        snackbar.show(error.message, "error");
      }
    }
  } finally {
    loading.value = false;
  }
}
</script>
